"use client"

import { useEffect, useRef, useState } from "react"
import { motion, useScroll, useTransform } from "framer-motion"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Image from "next/image"
import { ThreeCanvas } from "./three"
import { useLanguage } from "@/lib/i18n"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

// Animated gradient orbs with parallax depth
// Performance: Using CSS animations instead of JS for better battery life
const GradientOrbs = () => {
  const goldOrbRef = useRef<HTMLDivElement>(null)
  const tealOrbRef = useRef<HTMLDivElement>(null)
  const copperOrbRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Create parallax effect for each orb at different speeds
    const ctx = gsap.context(() => {
      // Gold orb - fastest parallax (foreground)
      if (goldOrbRef.current) {
        gsap.to(goldOrbRef.current, {
          y: 150,
          ease: "none",
          scrollTrigger: {
            trigger: "body",
            start: "top top",
            end: "50% top",
            scrub: 1,
          },
        })
      }

      // Teal orb - medium parallax (midground)
      if (tealOrbRef.current) {
        gsap.to(tealOrbRef.current, {
          y: 80,
          x: -30,
          ease: "none",
          scrollTrigger: {
            trigger: "body",
            start: "top top",
            end: "50% top",
            scrub: 1.5,
          },
        })
      }

      // Copper orb - slow parallax (background)
      if (copperOrbRef.current) {
        gsap.to(copperOrbRef.current, {
          y: 40,
          ease: "none",
          scrollTrigger: {
            trigger: "body",
            start: "top top",
            end: "50% top",
            scrub: 2,
          },
        })
      }
    })

    return () => ctx.revert()
  }, [])

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gold orb - top left (Layer 1 - closest) */}
      {/* Using CSS animations instead of Framer Motion for better performance */}
      <div
        ref={goldOrbRef}
        className="absolute w-[600px] h-[600px] rounded-full opacity-20 animate-float-slow"
        style={{
          background: "radial-gradient(circle, rgba(201,162,39,0.4) 0%, transparent 70%)",
          left: "-10%",
          top: "-20%",
          filter: "blur(80px)",
        }}
      />
      {/* Teal orb - bottom right (Layer 2 - middle) */}
      <div
        ref={tealOrbRef}
        className="absolute w-[500px] h-[500px] rounded-full opacity-15 animate-float-medium"
        style={{
          background: "radial-gradient(circle, rgba(42,157,143,0.4) 0%, transparent 70%)",
          right: "-5%",
          bottom: "10%",
          filter: "blur(100px)",
        }}
      />
      {/* Copper orb - center bottom (Layer 3 - farthest) */}
      {/* Using wrapper div for GSAP parallax to avoid transform conflicts with CSS centering */}
      <div
        ref={copperOrbRef}
        className="absolute"
        style={{
          left: "50%",
          bottom: "-10%",
          transform: "translateX(-50%)",
        }}
      >
        <div
          className="w-[400px] h-[400px] rounded-full opacity-10 animate-pulse-slow"
          style={{
            background: "radial-gradient(circle, rgba(184,115,51,0.3) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
      </div>
    </div>
  )
}

// Kinetic typography component - words animate in sequence
const KineticHeadline = ({ lines, delay = 0 }: { lines: string[]; delay?: number }) => {
  const { locale } = useLanguage()
  const containerRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const words = containerRef.current.querySelectorAll(".word")

    gsap.fromTo(
      words,
      {
        opacity: 0,
        y: 80,
        rotateX: -40,
        filter: "blur(10px)",
      },
      {
        opacity: 1,
        y: 0,
        rotateX: 0,
        filter: "blur(0px)",
        duration: 1,
        ease: "power4.out",
        stagger: 0.08,
        delay,
      }
    )
  }, [delay])

  return (
    <h1 ref={containerRef} className="perspective-[1000px]" aria-label={lines.join(" ")}>
      {lines.map((line, lineIndex) => (
        <span key={lineIndex} className="block overflow-hidden">
          <span className="flex flex-wrap justify-center gap-x-4 md:gap-x-6">
            {line.split(" ").map((word, wordIndex) => (
              <span
                key={wordIndex}
                className={`word inline-block text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-[var(--color-text-primary)]${locale !== "zh" ? " tracking-tight" : ""}`}
                style={{
                  fontFamily: "var(--font-display)",
                  lineHeight: 1.1,
                  ...(locale === "zh" ? { letterSpacing: "0.08em" } : {}),
                }}
              >
                {word}
              </span>
            ))}
          </span>
        </span>
      ))}
    </h1>
  )
}

// Accent text with gold color and subtle animation
const AccentTagline = ({ text, delay = 0 }: { text: string; delay?: number }) => {
  return (
    <motion.p
      className="text-lg sm:text-xl md:text-2xl font-medium text-[var(--color-accent-gold)] tracking-wide"
      style={{ fontFamily: "var(--font-mono)" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.p>
  )
}

export function HeroSection() {
  const { t, locale } = useLanguage()
  const [isLoaded, setIsLoaded] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const bgImageRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  })

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95])
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 100])

  // Background parallax - slower movement creates depth
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200])
  const bgScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1])

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  return (
    <section
      ref={sectionRef}
      data-section="hero"
      className="relative min-h-screen w-full overflow-hidden bg-[var(--color-bg-primary)]"
    >
      {/* Background Image Layer - with parallax */}
      <motion.div
        ref={bgImageRef}
        className="absolute inset-0 z-0"
        style={{ y: bgY, scale: bgScale }}
      >
        <Image
          src="/images/hero-background.png"
          alt=""
          fill
          priority
          className="object-cover object-center"
          style={{
            opacity: 0.15,
            mixBlendMode: "luminosity",
          }}
        />
        {/* Dark overlay for readability - adjusts based on theme */}
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-bg-primary)]/60 via-[var(--color-bg-primary)]/40 to-[var(--color-bg-primary)]/80" />
      </motion.div>

      {/* Animated gradient background */}
      <GradientOrbs />

      {/* 3D Floating geometric shapes */}
      <ThreeCanvas className="absolute inset-0 z-[2] pointer-events-none opacity-60" />

      {/* Subtle grid overlay - with slower parallax */}
      <motion.div
        ref={gridRef}
        className="absolute inset-0 opacity-[0.03] z-[1]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: "80px 80px",
          y: useTransform(scrollYProgress, [0, 1], [0, 100]),
        }}
      />

      {/* Main content */}
      <motion.div
        className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 md:px-16 text-center"
        style={{ paddingTop: "12vh", paddingBottom: "15vh", opacity, scale, y }}
      >
        <div className="max-w-5xl">
          {/* Main headline - Kinetic typography */}
          <div className="mb-8">
            <KineticHeadline lines={[t("hero.headline1"), t("hero.headline2")]} delay={0.3} />
          </div>

          {/* Subheadline with handwritten annotation */}
          <div className="relative mb-10">
            <motion.h2
              className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[var(--color-text-secondary)] leading-tight${locale !== "zh" ? " tracking-tight" : ""}`}
              style={{
                fontFamily: "var(--font-display)",
                letterSpacing: locale === "zh" ? "0.05em" : "-0.02em",
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 30 }}
              transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            >
              {t("hero.subheadline")}
            </motion.h2>
            {/* Handwritten annotation - human touch */}
            <motion.span
              className={`top-full mt-2 text-xl md:text-2xl text-[var(--color-accent-gold)]/70${locale !== "zh" ? " absolute -right-4 md:right-8 lg:right-16 rotate-[-3deg]" : " block text-center w-full"}`}
              style={{ fontFamily: "var(--font-handwritten)" }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: isLoaded ? 1 : 0, x: isLoaded ? 0 : -10 }}
              transition={{ duration: 0.6, delay: 1.8, ease: "easeOut" }}
            >
              {t("hero.annotation")}
            </motion.span>
          </div>

          {/* Accent divider */}
          <motion.div
            className="h-px bg-gradient-to-r from-transparent via-[var(--color-accent-gold)] to-transparent max-w-xs mx-auto mb-8"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: isLoaded ? 1 : 0, opacity: isLoaded ? 0.6 : 0 }}
            transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Tagline */}
          <AccentTagline text={t("hero.tagline")} delay={1.6} />

          {/* Stats with Caveat numbers */}
          <motion.p
            className="text-lg md:text-xl text-[var(--color-text-muted)] mt-8 max-w-xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: isLoaded ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 1.8 }}
          >
            <span
              className="text-3xl md:text-4xl text-[var(--color-text-secondary)]"
              style={{ fontFamily: "var(--font-handwritten)" }}
            >
              15
            </span>{" "}
            {t("hero.years")}{" "}
            <span
              className="text-3xl md:text-4xl text-[var(--color-text-secondary)]"
              style={{ fontFamily: "var(--font-handwritten)" }}
            >
              100+
            </span>{" "}
            {t("hero.projects")}{" "}
            <span
              className="text-3xl md:text-4xl text-[var(--color-text-secondary)]"
              style={{ fontFamily: "var(--font-handwritten)" }}
            >
              1
            </span>{" "}
            {t("hero.mission")}
          </motion.p>
        </div>
      </motion.div>
    </section>
  )
}
